import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import dayjs from 'dayjs';
import { gsap } from 'gsap';
import { FC, useRef } from 'react';
import InlineTags from '@/components/BlogPage/InlineTags';
import { BlogPostDto } from '@/components/BlogPage/types';
import Image from '@/components/Image';
import Link from '@/components/Link';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { Typography } from '@/components/shared/Typography';
import VimeoPlayer from '@/components/shared/VimeoPlayer';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useVimeoViewportPlayback } from '@/hooks/useVimeoViewportPlayback';
import { extractPlainTextFromBlocksContent } from '@/lib/blocks-content';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import css from './style.module.scss';
interface Props {
  post: BlogPostDto;
}
const PostHero: FC<Props> = t0 => {
  const $ = _c(38);
  const {
    post
  } = t0;
  const wrapperRef = useRef(null);
  const breadcrumbsRef = useRef(null);
  const heroVimeoPlaying = useVimeoViewportPlayback(wrapperRef);
  let t1;
  if ($[0] !== post.seo || $[1] !== post.title) {
    t1 = extractPlainTextFromBlocksContent(post.title) || post.seo.title;
    $[0] = post.seo;
    $[1] = post.title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const breadcrumbTitle = t1;
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!wrapperRef.current) {
        return;
      }
      const elements = [breadcrumbsRef.current, wrapperRef.current.querySelector(`.${css.backLink}`), wrapperRef.current.querySelector(`.${css.title}`), wrapperRef.current.querySelector(`.${css.infoWrapper}`), wrapperRef.current.querySelector(`.${css.heroMedia}`)].filter(Boolean);
      gsap.fromTo(elements, {
        opacity: 0,
        y: 40
      }, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.25,
        ease: "power2.out"
      });
    };
    t3 = {
      scope: wrapperRef
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useGSAP(t2, t3);
  let t4;
  if ($[5] !== post.heroMedia) {
    t4 = post.heroMedia && "mime" in post.heroMedia && post.heroMedia.mime.startsWith("video");
    $[5] = post.heroMedia;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isHeroVideo = t4;
  let t5;
  if ($[7] !== breadcrumbTitle) {
    t5 = breadcrumbTrails.blogPost(breadcrumbTitle);
    $[7] = breadcrumbTitle;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <PageBreadcrumbs ref={breadcrumbsRef} items={t5} withContainer={false} wrapperClassName={css.breadcrumbsWrap} />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== post.title) {
    t7 = <Typography variant="h3" tag="h1" className={css.title}><StrapiRichContent content={post.title} /></Typography>;
    $[11] = post.title;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== post.publishDate || $[14] !== post.publishedAt) {
    t8 = dayjs(post.publishDate ?? post.publishedAt).format("MMMM D, YYYY");
    $[13] = post.publishDate;
    $[14] = post.publishedAt;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <Typography variant="body-s" tag="p">{t8}</Typography>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== post.tags) {
    t10 = <Typography variant="body-s" className={css.tagsWrapper}><InlineTags tags={post.tags} /></Typography>;
    $[18] = post.tags;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== post.timeToRead) {
    t11 = post.timeToRead ? <Typography variant="body-s" tag="p" className={css.timeToRead}><img src="/assets/icons/blog/clock-icon.svg" height={16} width={16} alt="" /><span>{post.timeToRead}</span></Typography> : null;
    $[20] = post.timeToRead;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <div className={css.infoWrapper}>{t9}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== heroVimeoPlaying || $[27] !== isHeroVideo || $[28] !== post.alt || $[29] !== post.heroMedia) {
    t13 = isHeroVideo && post.heroMedia ? <VimeoPlayer url={post.heroMedia.url} playing={heroVimeoPlaying} /> : post.heroMedia && "width" in post.heroMedia && <Image width={post.heroMedia.width} height={post.heroMedia.height} src={post.heroMedia.url} alt={post.heroMedia.alternativeText ?? post.alt ?? "Hero image"} quality={100} />;
    $[26] = heroVimeoPlaying;
    $[27] = isHeroVideo;
    $[28] = post.alt;
    $[29] = post.heroMedia;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13) {
    t14 = <div className={css.heroMedia}>{t13}</div>;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t12 || $[34] !== t14 || $[35] !== t6 || $[36] !== t7) {
    t15 = <section ref={wrapperRef} className={css.wrapper}>{t6}{t7}{t12}{t14}</section>;
    $[33] = t12;
    $[34] = t14;
    $[35] = t6;
    $[36] = t7;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
};
export default PostHero;